import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../services/api';
import {
  TextField,
  NumberRangeField,
  DateRangeField,
  SelectField,
  BooleanField
} from '../components/DynamicFilterFields';
import type { DynamicFieldComponentProps } from '../components/DynamicFilterFields';
import type {
  KanbanFilterColumn,
  KanbanFilterFieldSchema,
  CardFiltersResponse,
  DynamicDateRangeValue,
  DynamicNumberRangeValue,
  DynamicFilterFieldValue,
  DynamicFilterInputValues,
  DynamicFilterPayload,
  FilterCondition
} from '../types';

type UseDynamicFiltersReturn = {
  columns: KanbanFilterColumn[];
  fields: KanbanFilterFieldSchema[];
  loading: boolean;
  error: string | null;
  buildPayload: (values: DynamicFilterInputValues) => DynamicFilterPayload;
  FieldComponents: Record<string, React.FC<DynamicFieldComponentProps>>;
  getDefaultValues: () => Record<string, DynamicFilterFieldValue>;
  normalizePersistedValues: (persisted: Record<string, unknown>) => Record<string, DynamicFilterFieldValue>;
  isValueEmpty: (value: unknown) => boolean;
};

const buildDefaultValue = (field: KanbanFilterFieldSchema): DynamicFilterFieldValue => {
  if (field.value_type === 'array' || field.type === 'multiselect' || field.type === 'checklist') {
    return [];
  }
  if (field.value_type === 'boolean' || field.type === 'checkbox' || field.type === 'booleano') {
    return undefined;
  }
  if (field.value_type === 'date' || field.value_type === 'datetime') {
    if (field.operators.includes('range')) {
      return { start: '', end: '' };
    }
    return '';
  }
  if (field.value_type === 'number' && field.operators.includes('range')) {
    return { min: undefined, max: undefined };
  }
  return '';
};

const isValueEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return isNaN(value);
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    if ('min' in value || 'max' in value) {
      const rangeValue = value as DynamicNumberRangeValue;
      return (!rangeValue.min || isNaN(Number(rangeValue.min))) &&
        (!rangeValue.max || isNaN(Number(rangeValue.max)));
    }
    if ('start' in value || 'end' in value) {
      const rangeValue = value as DynamicDateRangeValue;
      return (!rangeValue.start || rangeValue.start === '') &&
        (!rangeValue.end || rangeValue.end === '');
    }
  }
  return false;
};

export function useDynamicFilters(boardId?: number): UseDynamicFiltersReturn {
  const [schema, setSchema] = useState<CardFiltersResponse['data'] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!boardId) return;

    let mounted = true;

    const fetchSchema = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<CardFiltersResponse>(`/api/adm/boards/${boardId}/card-filters`);
        if (!mounted) return;

        if (response.data.success) {
          setSchema(response.data.data);
        } else {
          setError('Não foi possível carregar o schema de filtros.');
        }
      } catch {
        if (mounted) {
          setError('Não foi possível carregar os filtros no momento.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSchema();

    return () => {
      mounted = false;
    };
  }, [boardId]);

  const getDefaultValues = useCallback((): Record<string, DynamicFilterFieldValue> => {
    const result: Record<string, DynamicFilterFieldValue> = {};
    (schema?.fields || []).forEach(field => {
      result[`field_${field.id}`] = buildDefaultValue(field);
    });
    return result;
  }, [schema?.fields]);

  const normalizePersistedValues = useCallback((persisted: Record<string, unknown>): Record<string, DynamicFilterFieldValue> => {
    const defaults = getDefaultValues();

    if (!persisted) return defaults;

    const normalized: Record<string, DynamicFilterFieldValue> = { ...defaults };

    (schema?.fields || []).forEach(field => {
      const fieldKey = `field_${field.id}`;
      const persistedValue = persisted[fieldKey];

      if (persistedValue === undefined || persistedValue === null) return;

      if (field.value_type === 'number' && field.operators.includes('range')) {
        const obj = persistedValue as DynamicNumberRangeValue;
        normalized[fieldKey] = {
          min: obj.min !== undefined ? Number(obj.min) : undefined,
          max: obj.max !== undefined ? Number(obj.max) : undefined
        };
      } else if ((field.value_type === 'date' || field.value_type === 'datetime') && field.operators.includes('range')) {
        const obj = persistedValue as DynamicDateRangeValue;
        normalized[fieldKey] = {
          start: obj.start || '',
          end: obj.end || ''
        };
      } else if (field.value_type === 'array' || field.type === 'multiselect' || field.type === 'checklist') {
        normalized[fieldKey] = Array.isArray(persistedValue) ? persistedValue : [];
      } else if (field.value_type === 'boolean' || field.type === 'checkbox' || field.type === 'booleano') {
        normalized[fieldKey] = persistedValue as DynamicFilterFieldValue;
      } else if (field.value_type === 'number') {
        const str = String(persistedValue).trim();
        if (str === '') {
          normalized[fieldKey] = '';
        } else {
          const num = Number(str);
          normalized[fieldKey] = isNaN(num) ? '' : num;
        }
      } else {
        normalized[fieldKey] = String(persistedValue || '');
      }
    });

    return normalized;
  }, [schema?.fields, getDefaultValues]);

  const determineOperator = useCallback((field: KanbanFilterFieldSchema, value: DynamicFilterFieldValue): string => {
    let operator = 'eq';

    if (value === undefined || value === null) return operator;

    if (field.value_type === 'number' && field.operators.includes('range') &&
      typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const rangeValue = value as DynamicNumberRangeValue;
      if (rangeValue.min !== undefined && rangeValue.max !== undefined) {
        return 'between';
      } else if (rangeValue.min !== undefined) {
        return 'gte';
      } else if (rangeValue.max !== undefined) {
        return 'lte';
      }
    } else if ((field.value_type === 'date' || field.value_type === 'datetime') &&
      field.operators.includes('range') && typeof value === 'object' && !Array.isArray(value)) {
      const rangeValue = value as DynamicDateRangeValue;
      if (rangeValue.start && rangeValue.end) {
        return 'between';
      } else if (rangeValue.start) {
        return 'gte';
      } else if (rangeValue.end) {
        return 'lte';
      }
    } else if (field.value_type === 'array' || field.type === 'multiselect' || field.type === 'checklist') {
      return 'in';
    }

    if (field.operators && field.operators.length > 0) {
      if (typeof value === 'string' && (value.includes('%') || value.includes('*'))) {
        if (field.operators.includes('like')) return 'like';
      }
      if (!field.operators.includes(operator)) {
        return field.operators[0];
      }
    }

    return operator;
  }, []);

  const buildPayload = useCallback((values: DynamicFilterInputValues): DynamicFilterPayload => {
    const payload: DynamicFilterPayload = {
      board_id: boardId || 0,
      per_page: 50,
    };

    const search = typeof values.search === 'string' ? values.search.trim() : '';
    if (search) payload.search = search;

    const selectedColumns = Array.isArray(values.selectedColumns)
      ? values.selectedColumns.map(value => Number(value)).filter(value => !isNaN(value))
      : [];

    if (selectedColumns.length > 0) {
      payload.column_ids = selectedColumns;
    }

    const filters: FilterCondition[] = [];

    (schema?.fields || []).forEach(field => {
      const fieldKey = `field_${field.id}` as keyof DynamicFilterInputValues;
      const fieldValue = values[fieldKey];

      if (isValueEmpty(fieldValue)) return;

      let processedValue = fieldValue;
      if (field.type === 'telefone' || field.label.toLowerCase().includes('telefone') || field.label.toLowerCase().includes('celular')) {
        if (typeof fieldValue === 'string') {
          processedValue = fieldValue.replace(/\D/g, '');
        }
      }

      if (fieldValue === undefined || fieldValue === null) return;
      if (typeof fieldValue === 'boolean' && fieldValue === false) return;

      const operator = determineOperator(field, processedValue);

      filters.push({
        field_id: field.id,
        operator,
        value: processedValue
      });
    });

    if (filters.length > 0) {
      payload.filters = filters;
    }

    return payload;
  }, [boardId, schema?.fields, determineOperator, isValueEmpty]);

  const FieldComponents = useMemo((): Record<string, React.FC<DynamicFieldComponentProps>> => {
    const components: Record<string, React.FC<DynamicFieldComponentProps>> = {};

    (schema?.fields || []).forEach(field => {
      const fieldKey = `field_${field.id}`;

      if (field.value_type === 'number' && field.operators.includes('range')) {
        components[fieldKey] = NumberRangeField;
      } else if ((field.value_type === 'date' || field.value_type === 'datetime') && field.operators.includes('range')) {
        components[fieldKey] = DateRangeField;
      } else if (field.type === 'select' || field.type === 'radio') {
        components[fieldKey] = SelectField;
      } else if (field.value_type === 'boolean' || field.type === 'checkbox' || field.type === 'booleano') {
        components[fieldKey] = BooleanField;
      } else {
        components[fieldKey] = TextField;
      }
    });

    return components;
  }, [schema?.fields]);

  return {
    columns: schema?.columns || [],
    fields: schema?.fields || [],
    loading,
    error,
    buildPayload,
    FieldComponents,
    getDefaultValues,
    normalizePersistedValues,
    isValueEmpty,
  };
}